import {
  AUTH_NOT_LOGGED_IN_ERROR_CODE,
  AUTH_PROJECT_SESSION_EXPIRED_ERROR_CODE,
  APP_VALIDATION_ERROR_CODE,
  INTERFACES_DOMAIN_IN_USE_ERROR_CODE,
  INTERFACES_PAGE_SLUG_IN_USE_ERROR_CODE,
  INTERFACES_SLUG_IN_USE_ERROR_CODE,
  UNKNOWN_ERROR_CODE,
} from "@/lib/errorCodes";
import { TRPCClientError } from "@trpc/client";
import { TRPC_ERROR_CODE_KEY } from "@trpc/server/rpc";
import cuid from "cuid";
import { isNumber, isObject } from "lodash";
import { ZodError, ZodIssue } from "zod";

export const UNKNOWN_ERROR_STATUS = 500;
export const UNKNOWN_ERROR_MSG = "An unexpected error occurred";

export interface AppErrorArgs {
  code?: string;
  status?: number;
  message?: string;
  data?: Record<string, any>;
  cause?: unknown;
}

export class AppError extends Error {
  public name = "AppError";
  public code: string;
  public status: number;
  public id = cuid.slug();
  public trpcCode: TRPC_ERROR_CODE_KEY = "INTERNAL_SERVER_ERROR";
  public data: Record<string, any> = {};

  constructor({ code, status, message, cause, data }: AppErrorArgs = {}) {
    if (!message) {
      if (cause instanceof Error || isZodIssue(cause)) {
        message = cause.message;
      } else {
        message = UNKNOWN_ERROR_MSG;
      }
    }

    const causeError = MUTATES_normalizeErrorCause(cause);
    super(message, { cause: causeError });

    this.code = code ?? codeFromError(cause);
    this.status = status ?? UNKNOWN_ERROR_STATUS;
    this.trpcCode = trpcCodeFromStatus(this.status);
    this.addData(data ?? {});
  }

  public addData(data: Record<string, any>) {
    this.data = { ...this.data, ...data };
  }

  public toJSON() {
    return {
      id: this.id,
      name: this.name,
      status: this.status,
      code: this.code,
      message: this.message,
      data: this.data,
      cause: this.cause,
    };
  }
}

type ErrorLike = { message: string } & Record<string, unknown>;

/**
 * Pino serializer does not support cause where there is no `message` property or the `cause` is not an `Error`.
 * See https://github.com/pinojs/pino-std-serializers/issues/158
 *
 * Note that `cause` values that are not `Error`s are supported.
 * See MDN -> https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause#providing_structured_data_as_the_error_cause
 *
 * To enable us to pass those non-error `cause` values to pino, we have to ensure that each `cause` has a `message` property, so pino deems it "ErrorLike".
 * https://github.com/pinojs/pino-std-serializers/blob/a7cfba4de6a6f9334de81984aa84d3c21c562b9d/lib/err-with-cause.js#L28
 *
 * As an alternative, we could ensure the `cause` is an `Error` (deeply nested check),
 * but if the `cause` is an `Error` and we create a synthetic error to be its cause (if the cause of that error is not an `Error`), the stacktrace would be incorrect – it would point to this function!
 *
 * So, to reduce confusion, we have decided to make sure that each `cause` has a `message` property, so pino deems it "ErrorLike".
 * This way, we are not creating synthetic errors with confusing stacktraces.
 */
function MUTATES_normalizeErrorCause(cause: unknown): Error | ErrorLike | null {
  function ensureMessage(obj: any) {
    if (typeof obj !== "object" || obj == null) {
      return;
    }

    if (!("message" in obj) || typeof obj.message !== "string") {
      obj.message = "SyntheticCauseError";
    }

    if ("cause" in obj) {
      ensureMessage(obj.cause);
    }
  }

  if (typeof cause !== "object") {
    if (cause === undefined) {
      return null;
    }

    return { message: "SyntheticCauseError", data: cause };
  }

  if (cause == null) {
    return null;
  }

  if (cause instanceof Error) {
    ensureMessage(cause);
    return cause;
  }

  ensureMessage(cause);
  return cause as ErrorLike;
}

/**
 * This error indicates that the user is not logged in to the Zapier monolith, and
 * must be redirected to the login page.
 */
export class NotLoggedInError extends AppError {
  name = "NotLoggedInError";
  code = AUTH_NOT_LOGGED_IN_ERROR_CODE;
  status = 401;

  constructor() {
    super({ message: "not logged in" });
  }
}

export class ProjectSessionExpiredErrorTrpc extends AppError {
  name = "ProjectSessionExpiredError";
  code = AUTH_PROJECT_SESSION_EXPIRED_ERROR_CODE;
  status = 401;

  constructor() {
    super({
      message: "Project session expired",
    });
  }
}

export function isTrpcProjectSessionExpiredError(error: unknown) {
  if (error instanceof TRPCClientError && error.data?.code) {
    switch (error.data.code) {
      case AUTH_PROJECT_SESSION_EXPIRED_ERROR_CODE:
        return true;
    }
  }

  return false;
}

export function isTrpcClientError(error: unknown) {
  if (
    error instanceof TRPCClientError &&
    isNumber(error.data?.httpStatus) &&
    error.data?.httpStatus >= 400 &&
    error.data?.httpStatus < 500
  ) {
    return false;
  }

  return true;
}

export class DomainInUseError extends AppError {
  name = "DomainInUseError";
  code = INTERFACES_DOMAIN_IN_USE_ERROR_CODE;
  status = 400;

  constructor() {
    super({
      message:
        "That custom domain is already claimed by another Interfaces project.",
    });
  }
}

// Technically 408 is sent by server on idle connection. But we are using it here to represent a timeout error.
// If this is changed to a different status code, the retryable error codes should be updated as well.
export class AbortError extends AppError {
  constructor() {
    super({ status: 408, message: "Request Timeout" });
  }
}

export class ProjectSlugInUseError extends AppError {
  name = "ProjectSlugInUseError";
  code = INTERFACES_SLUG_IN_USE_ERROR_CODE;
  status = 400;

  constructor() {
    super({
      message: "Project slug is not available.",
    });
  }
}

export class PageSlugInUseError extends AppError {
  name = "PageSlugInUseError";
  code = INTERFACES_PAGE_SLUG_IN_USE_ERROR_CODE;
  status = 400;

  constructor() {
    super({
      message: "Page slug is not available.",
    });
  }
}

export const RETRYABLE_ERROR_CODES = [408, 429, 500, 502, 503, 504];
const HTTP_STATUS_TO_JSONRPC2: Record<number, TRPC_ERROR_CODE_KEY> = {
  400: "BAD_REQUEST",
  404: "NOT_FOUND",
  500: "INTERNAL_SERVER_ERROR",
  401: "UNAUTHORIZED",
  403: "FORBIDDEN",
  408: "TIMEOUT",
  409: "CONFLICT",
  499: "CLIENT_CLOSED_REQUEST",
  412: "PRECONDITION_FAILED",
  413: "PAYLOAD_TOO_LARGE",
  405: "METHOD_NOT_SUPPORTED",
  429: "TOO_MANY_REQUESTS",
};

const trpcCodeFromStatus = (status: number): TRPC_ERROR_CODE_KEY =>
  HTTP_STATUS_TO_JSONRPC2[status] ?? "INTERNAL_SERVER_ERROR";

function codeFromError(err: unknown): string {
  if (err instanceof AppError) {
    return err.code;
  }

  if (err instanceof Error && err.hasOwnProperty("code")) {
    return (err as any).code;
  }

  if (err instanceof ZodError) {
    return APP_VALIDATION_ERROR_CODE;
  }

  if (isZodIssue(err)) {
    return err.code;
  }

  return UNKNOWN_ERROR_CODE;
}

export function isZodIssue(e: unknown): e is ZodIssue {
  if (!isObject(e)) return false;

  return (
    "message" in e &&
    typeof e.message === "string" &&
    "path" in e &&
    Array.isArray(e.path) &&
    e.path.every((p) => typeof p === "string" || typeof p === "number")
  );
}
